<template>
    <div>
        <div v-if="filters.length" class="w-full to_md:mb-8 md:w-56 xl:w-64 md:mr-16">
            <div class="border border-secondary-500 text-black md:border-0">
                <button class="flex justify-between md:hidden w-full p-3 items-center border-0 text-black outline-none focus:outline-none rounded-none" @click.prevent="onToggleFilters">
                    <span>Filters</span>
                    <svg
                        aria-hidden="true" focusable="false" data-prefix="fas" data-icon="heart" class="w-5 h-5" role="img"
                        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                    >
                        <path d="M4 5L10 5M10 5C10 6.10457 10.8954 7 12 7C13.1046 7 14 6.10457 14 5M10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5M14 5L20 5M4 12H16M16 12C16 13.1046 16.8954 14 18 14C19.1046 14 20 13.1046 20 12C20 10.8954 19.1046 10 18 10C16.8954 10 16 10.8954 16 12ZM8 19H20M8 19C8 17.8954 7.10457 17 6 17C4.89543 17 4 17.8954 4 19C4 20.1046 4.89543 21 6 21C7.10457 21 8 20.1046 8 19Z" stroke="#000000" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                </button>
                <div class="border-t border-secondary-500 p-3 md:p-0" :class="[isFiltersShown? 'block': 'hidden md:block']">
                    <aside v-if="filters.length > 0">
                        <AccordionRoot type="multiple">
                            <AccordionItem v-for="filterItem in filters" :key="filterItem.field" v-slot="{open}" :value="filterItem.field">
                                <AccordionHeader>
                                    <AccordionTrigger class="flex items-center justify-between w-full text-left h6 py-3 border-b border-secondary-300">
                                        <span>{{ filterItem.title }}</span>
                                        <span>
                                            <svg
                                                aria-hidden="true"
                                                focusable="false"
                                                class="block w-4 h-4 transition-transform duration-300 ease-in-out"
                                                :class="{ 'rotate-180': open }"
                                                role="img"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 448 512"
                                            >
                                                <path fill="currentColor" d="M441.9 167.3l-19.8-19.8c-4.7-4.7-12.3-4.7-17 0L224 328.2 42.9 147.5c-4.7-4.7-12.3-4.7-17 0L6.1 167.3c-4.7 4.7-4.7 12.3 0 17l209.4 209.4c4.7 4.7 12.3 4.7 17 0l209.4-209.4c4.7-4.7 4.7-12.3 0-17z" />
                                            </svg>
                                        </span>
                                    </AccordionTrigger>
                                </AccordionHeader>

                                <AccordionContent>
                                    <div class="py-4">
                                        <ul>
                                            <li v-for="category in filterItem.categories" :key="category.id">
                                                <div class="fui-checkbox">
                                                    <input
                                                        :id="`category-${category.id}`"
                                                        :name="`${filterItem.field}[]`"
                                                        :value="category.id"
                                                        :checked="filter[filterItem.field].includes(String(category.id))"
                                                        type="checkbox"
                                                        @change="updateFilter(filterItem.field, category.id)"
                                                    >

                                                    <label
                                                        :for="`category-${category.id}`"
                                                        class="cursor-pointer"
                                                    >{{ category.title }}</label>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </AccordionRoot>
                    </aside>
                </div>
            </div>
        </div>

        <section ref="productView" class="block md:flex-1">
            <div v-if="filters.length > 0" class="lg:flex justify-between mb-8">
                <p v-if="query" class="to_lg:mb-2">Searching for "{{ query }}".</p>
                <p v-else class="to_lg:mb-2">Search for your perfect tile your way.</p>

                <div class="fui-field fui-label-floating fui-floating fui-select-field px-0">
                    <div class="fui-input-container">
                        <label class="fui-label" for="order-by">Sort by</label>

                        <select id="order-by" v-model="orderBy" class="fui-select">
                            <option value="RAND()">Random</option>
                            <option value="title ASC">Alphabetically (A-Z)</option>
                            <option value="title DESC">Alphabetically (Z-A)</option>
                            <option value="priceSet ASC">Price (Low to high)</option>
                            <option value="priceSet DESC">Price (High to low)</option>
                        </select>
                    </div>
                </div>
            </div>

            <div class="shop-products">
                <div v-if="$apollo.loading" class="shop-loading">
                    <span class="loading loading-lg"></span>
                </div>

                <ShopProduct
                    v-for="product in products"
                    :key="product.id"
                    class="shop-products__item"
                    :product="product.element ? product.element : product"
                />

                <p v-if="!$apollo.loading && products.length === 0 && type === 'wishlist'" class="text-center flex-1">
                    You have no products in your wishlist.
                </p>

                <p v-else-if="!$apollo.loading && products.length === 0" class="flex-1">
                    Sorry, no tiles could be found for the {{ query ? 'entered query' : 'selected options' }}.
                </p>
            </div>

            <pagination
                :items="count"
                :items-per-page="16"
                :current="page"
                @changed="pageChanged"
            />
        </section>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import qs from 'qs';

import { PRODUCTS, SAMPLE_PRODUCTS } from '../../graphql/products';
import { WISHLIST_PRODUCTS } from '../../graphql/wishlistProducts';

import {
    AccordionContent,
    AccordionHeader,
    AccordionItem,
    AccordionRoot,
    AccordionTrigger,
} from 'reka-ui';
import Pagination from './Pagination.vue';
import ShopProduct from './ShopProduct.vue';

export default {
    components: {
        AccordionContent,
        AccordionHeader,
        AccordionItem,
        AccordionRoot,
        AccordionTrigger,
        ShopProduct,
        Pagination,
    },

    props: {
        type: {
            type: String,
            default: 'tile',
        },

        filters: {
            type: Array,
            default: () => { return ([]); },
        },

        sessionId: {
            type: String,
            default: '',
        },
    },

    apollo: {
        products: {
            query() {
                let query;

                switch (this.type) {
                    case 'wishlist':
                        query = WISHLIST_PRODUCTS;
                        break;
                    case 'samples':
                        query = SAMPLE_PRODUCTS;
                        break;
                    default:
                        query = PRODUCTS;
                }

                return query;
            },
            manual: true,
            variables() {
                return this.variables;
            },
            result({ data, loading }) {
                if (!loading && data) {
                    if (data.products.length) {
                        if (this.type === 'wishlist') {
                            this.products = data.products[0].items;
                        } else {
                            this.products = data.products;
                        }
                    }

                    this.count = data.count;
                }
            },
            error(error) {
                console.error('We‘ve got an error!', error);
            },
        },
    },

    data() {
        const filter = {};

        for (const shopFilter of this.filters) {
            filter[shopFilter.field] = typeof shopFilter.default !== 'undefined' ? shopFilter.default : [];
        }

        return {
            count: 0,
            page: 1,
            products: [],
            orderBy: 'RAND()',
            query: null,
            filter,
            mounted: false,
            variables: {
                limit: 16,
                offset: 16 * (this.page - 1),
                search: this.query,
                orderBy: this.orderBy,
            },
            isFiltersShown: false,
        };
    },

    watch: {
        filter: {
            deep: true,
            handler: debounce(function() {
                // this.page = 1;

                if (this.mounted) {
                    this.load(true);
                }
            }, 700),
        },
        orderBy() {
            // this.page = 1;
            this.load();
        },
    },

    created() {
        this.loadParams();
        this.load();

        // Prevent loading multiple times on-load due to filters being populated
        setTimeout(() => {
            this.mounted = true;
        }, 1000);
    },

    methods: {
        loadParams() {
            const params = qs.parse(location.search, {
                ignoreQueryPrefix: true,
            });

            if (typeof params.orderBy !== 'undefined') {
                this.orderBy = params.orderBy;
                delete params.orderBy;
            }

            if (typeof params.q !== 'undefined') {
                this.query = params.q;
                delete params.q;
            }

            if (typeof params.page !== 'undefined') {
                this.page = parseInt(params.page, 10);
                delete params.page;
            }

            for (const key in params) {
                if (Object.prototype.hasOwnProperty.call(params, key)) {
                    const value = params[key];

                    console.log(key, value);

                    if (typeof this.filter[key] !== 'undefined') {
                        this.filter[key] = value;
                    }
                }
            }
        },

        load(scrollToTop = false) {

            this.variables = {
                ...this.variables,
                limit: 16,
                offset: 16 * (this.page - 1),
                search: this.query,
                orderBy: this.orderBy,
            };

            const params = {
                orderBy: this.orderBy,
            };

            if (this.query) {
                params.q = this.query;
            }

            if (this.page) {
                params.page = this.page;
            }

            for (const key in this.filter) {
                if (Object.prototype.hasOwnProperty.call(this.filter, key)) {
                    const value = this.filter[key];

                    if (value.length > 0) {
                        if (key === 'price') {
                            this.variables.price = [
                                'and',
                                `>= ${value[0]}`,
                                `<= ${value[1]}`,
                            ];
                        } else {
                            this.variables[key] = value;
                        }

                        params[key] = value;
                    }
                }
            }

            // For wishlists, attach the user/session to restrict by
            if (this.type === 'wishlist') {
                this.variables.sessionId = this.sessionId;
            }

            history.replaceState(null, '', `?${qs.stringify(params)}`);

            this.$apollo.queries.products.refetch();

            if (scrollToTop) {
                this.$nextTick(() => {
                    // this.$el.scrollIntoView({
                    this.$refs.productView.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'start',
                    });
                });
            }
        },

        updateFilter(field, value) {
            const prevValue = this.filter[field] || [];

            if (prevValue.length && prevValue.includes(String(value))) {
                const index = prevValue.indexOf(String(value));

                if (index !== -1) {
                    prevValue.splice(index, 1);
                }

                this.filter[field] = prevValue;

                return;
            }

            this.filter[field] = [...prevValue, String(value)];
        },

        pageChanged(page) {
            this.page = page;
            this.load(true);
        },

        onToggleFilters() {
            this.isFiltersShown = !this.isFiltersShown;
        },
    },
};
</script>
